import { useEffect, useMemo, useRef, useState } from "react";
import {
  LayoutDashboard, Users, FileEdit, Send, Settings as SettingsIcon, BarChart3, Mail,
  Plus, Trash2, Search, Download, Upload, X, Eye, Copy, Play, Pause, ChevronDown,
  CheckCircle2, AlertCircle, Loader2, Pencil, ArrowLeft, Image as ImageIcon, Heading1,
  Type, Link2, Minus, MoveVertical, RefreshCw, Check, MailCheck, MousePointerClick,
  ShieldCheck, ShieldAlert, ShieldX,
} from "lucide-react";

type NlView = "dashboard" | "subscribers" | "templates" | "campaigns" | "smtp" | "reports" | "settings";

const API = `${import.meta.env.BASE_URL}api`;

async function api(path: string, opts: RequestInit = {}): Promise<any> {
  const res = await fetch(`${API}${path}`, {
    credentials: "include",
    headers: opts.body ? { "Content-Type": "application/json", ...(opts.headers || {}) } : opts.headers,
    ...opts,
  });
  const text = await res.text();
  let data: any = null;
  try { data = text ? JSON.parse(text) : null; } catch { data = text; }
  if (!res.ok) throw new Error((data && data.error) || `Request failed: ${res.status}`);
  return data;
}

async function compressImage(file: File, maxWidth = 1400, quality = 0.82): Promise<{ blob: Blob; dataUrl: string; width: number; height: number; mimeType: string }> {
  const dataUrl0 = await new Promise<string>((res, rej) => { const r = new FileReader(); r.onload = () => res(String(r.result)); r.onerror = () => rej(new Error("Read failed")); r.readAsDataURL(file); });
  if (/^image\/gif/i.test(file.type)) {
    const blob = await (await fetch(dataUrl0)).blob();
    return { blob, dataUrl: dataUrl0, width: 0, height: 0, mimeType: "image/gif" };
  }
  const img = await new Promise<HTMLImageElement>((res, rej) => { const i = new Image(); i.onload = () => res(i); i.onerror = () => rej(new Error("Image load failed")); i.src = dataUrl0; });
  const ratio = Math.min(1, maxWidth / img.naturalWidth);
  const w = Math.round(img.naturalWidth * ratio);
  const h = Math.round(img.naturalHeight * ratio);
  const canvas = document.createElement("canvas");
  canvas.width = w; canvas.height = h;
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("Canvas not available");
  ctx.fillStyle = "#ffffff"; ctx.fillRect(0, 0, w, h);
  ctx.drawImage(img, 0, 0, w, h);
  const hasAlpha = /^image\/png|webp/i.test(file.type);
  const targetMime = hasAlpha ? "image/webp" : "image/jpeg";
  const blob: Blob = await new Promise((res, rej) => canvas.toBlob(b => b ? res(b) : rej(new Error("Encode failed")), targetMime, quality));
  const dataUrl: string = await new Promise((res, rej) => { const r = new FileReader(); r.onload = () => res(String(r.result)); r.onerror = () => rej(new Error("Read failed")); r.readAsDataURL(blob); });
  return { blob, dataUrl, width: w, height: h, mimeType: targetMime };
}

async function uploadImageFile(file: File): Promise<{ url: string; width: number; height: number }> {
  const c = await compressImage(file);
  const base64 = c.dataUrl.split(",")[1] || "";
  const ext = c.mimeType === "image/webp" ? "webp" : c.mimeType === "image/gif" ? "gif" : "jpg";
  const fileName = (file.name || "image").replace(/\.[a-z0-9]+$/i, "") + "." + ext;
  const r = await api("/admin/newsletter/images", { method: "POST", body: JSON.stringify({ fileName, mimeType: c.mimeType, data: base64, width: c.width, height: c.height }) });
  return { url: r.url, width: r.width || c.width, height: r.height || c.height };
}

function pickImageFile(): Promise<File | null> {
  return new Promise(res => {
    const input = document.createElement("input");
    input.type = "file"; input.accept = "image/*";
    input.onchange = () => { const f = input.files?.[0] || null; res(f); };
    input.oncancel = () => res(null);
    input.click();
  });
}

type Sub = { id: number; email: string; firstName: string | null; lastName: string | null; status: string; source: string | null; groups: string[] | null; createdAt: string };
type Group = { id: number; name: string };
const FONT_OPTIONS: { label: string; value: string }[] = [
  { label: "Default", value: "" },
  { label: "Arial", value: "Arial, Helvetica, sans-serif" },
  { label: "Helvetica", value: "Helvetica, Arial, sans-serif" },
  { label: "Verdana", value: "Verdana, Geneva, sans-serif" },
  { label: "Tahoma", value: "Tahoma, Geneva, sans-serif" },
  { label: "Trebuchet MS", value: "'Trebuchet MS', sans-serif" },
  { label: "Georgia", value: "Georgia, serif" },
  { label: "Times New Roman", value: "'Times New Roman', Times, serif" },
  { label: "Courier New", value: "'Courier New', Courier, monospace" },
  { label: "Garamond", value: "Garamond, serif" },
];
const HEADING_SIZES = [16, 18, 20, 22, 24, 28, 32, 36, 40, 48, 56, 64];
const PARAGRAPH_SIZES = [12, 13, 14, 15, 16, 17, 18, 20, 22, 24];

type Template = { id: number; name: string; subject: string; preheader: string | null; isPlainText: boolean; bodyText: string | null; signatureHtml: string | null; blocks: any[] | null; updatedAt: string };
type Campaign = { id: number; name: string; templateId: number | null; status: string; totalRecipients: number; sentCount: number; failedCount: number; opensCount: number; clicksCount: number; groupFilter: string[] | null; createdAt: string };
type SettingsRow = { id: number; showHeader: boolean; logoUrl: string | null; logoWidth: number; headerBackground: string; accentColor: string; showAccentBar: boolean; socialLinks: Array<{ network: string; url: string }> | null; businessName: string | null; businessAddress: string | null; defaultFromName: string | null; defaultFromEmail: string | null; replyToEmail: string | null; emailsPerMinute: number; trackOpens: boolean; trackClicks: boolean };

const STATUS_COLORS: Record<string, string> = {
  subscribed: "bg-emerald-100 text-emerald-700",
  unsubscribed: "bg-slate-200 text-slate-600",
  bounced: "bg-amber-100 text-amber-700",
  complained: "bg-rose-100 text-rose-700",
  pending: "bg-blue-100 text-blue-700",
  draft: "bg-slate-100 text-slate-600",
  sending: "bg-blue-100 text-blue-700",
  completed: "bg-emerald-100 text-emerald-700",
  cancelled: "bg-rose-100 text-rose-700",
};

function Badge({ children, tone = "subscribed" }: { children: React.ReactNode; tone?: string }) {
  return <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_COLORS[tone] || "bg-slate-100 text-slate-600"}`}>{children}</span>;
}

function Toast({ msg, kind = "ok", onClose }: { msg: string; kind?: "ok" | "err"; onClose: () => void }) {
  useEffect(() => { const t = setTimeout(onClose, 3500); return () => clearTimeout(t); }, [onClose]);
  return (
    <div className={`fixed top-4 right-4 z-[100] px-4 py-3 rounded-lg shadow-lg flex items-center gap-2 text-sm font-medium ${kind === "ok" ? "bg-emerald-600 text-white" : "bg-rose-600 text-white"}`}>
      {kind === "ok" ? <CheckCircle2 className="w-4 h-4" /> : <AlertCircle className="w-4 h-4" />}
      {msg}
    </div>
  );
}

function Modal({ open, onClose, title, children, size = "md" }: { open: boolean; onClose: () => void; title: string; children: React.ReactNode; size?: "sm" | "md" | "lg" | "xl" }) {
  if (!open) return null;
  const w = size === "sm" ? "max-w-sm" : size === "lg" ? "max-w-3xl" : size === "xl" ? "max-w-5xl" : "max-w-lg";
  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={onClose}>
      <div className={`bg-white rounded-xl shadow-xl w-full ${w} max-h-[90vh] flex flex-col`} onClick={e => e.stopPropagation()}>
        <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between">
          <h3 className="font-bold text-slate-900">{title}</h3>
          <button onClick={onClose} className="p-1 rounded hover:bg-slate-100"><X className="w-5 h-5 text-slate-500" /></button>
        </div>
        <div className="px-5 py-4 overflow-y-auto">{children}</div>
      </div>
    </div>
  );
}

export default function AdminNewsletter({ view = "dashboard", onNavigate }: { view?: NlView; onNavigate?: (v: NlView) => void } = {}) {
  const [toast, setToast] = useState<{ msg: string; kind: "ok" | "err" } | null>(null);
  const notify = (msg: string, kind: "ok" | "err" = "ok") => setToast({ msg, kind });
  const nav = onNavigate || (() => {});
  return (
    <div className="space-y-4">
      {toast && <Toast msg={toast.msg} kind={toast.kind} onClose={() => setToast(null)} />}
      {view === "dashboard" && <DashboardView notify={notify} onNavigate={nav} />}
      {view === "subscribers" && <SubscribersView notify={notify} />}
      {view === "templates" && <TemplatesView notify={notify} />}
      {view === "campaigns" && <CampaignsView notify={notify} />}
      {view === "smtp" && <SmtpView notify={notify} />}
      {view === "reports" && <ReportsView notify={notify} />}
      {view === "settings" && <SettingsView notify={notify} />}
    </div>
  );
}

export type NewsletterView = NlView;

// ---------------- Dashboard ----------------
function StatTile({ label, value, icon: Icon, tone = "blue" }: { label: string; value: number | string; icon: typeof Users; tone?: string }) {
  const toneCls: Record<string, string> = {
    blue: "bg-blue-50 text-blue-600", emerald: "bg-emerald-50 text-emerald-600",
    amber: "bg-amber-50 text-amber-600", rose: "bg-rose-50 text-rose-600",
    purple: "bg-purple-50 text-purple-600", cyan: "bg-cyan-50 text-cyan-600",
  };
  return (
    <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm">
      <div className="flex items-center justify-between">
        <div className={`p-2 rounded-lg ${toneCls[tone]}`}><Icon className="w-5 h-5" /></div>
      </div>
      <div className="mt-3 text-2xl font-bold text-slate-900">{value}</div>
      <div className="text-xs text-slate-500 mt-0.5">{label}</div>
    </div>
  );
}

function DashboardView({ notify, onNavigate }: { notify: (m: string, k?: "ok" | "err") => void; onNavigate: (v: NlView) => void }) {
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const load = async () => { setLoading(true); try { setData(await api("/admin/newsletter/dashboard")); } catch (e: any) { notify(e.message, "err"); } finally { setLoading(false); } };
  useEffect(() => { load(); }, []);
  if (loading) return <div className="p-12 text-center text-slate-500"><Loader2 className="w-6 h-6 animate-spin inline" /></div>;
  if (!data) return null;
  const s = data.subscribers; const c = data.campaigns;
  return (
    <div className="space-y-5">
      <div>
        <h2 className="text-sm font-semibold text-slate-700 mb-2">Subscribers</h2>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
          <StatTile label="Total" value={s.total} icon={Users} tone="blue" />
          <StatTile label="Active" value={s.active} icon={CheckCircle2} tone="emerald" />
          <StatTile label="Unsubscribed" value={s.unsubscribed} icon={X} tone="rose" />
          <StatTile label="Bounced" value={s.bounced} icon={AlertCircle} tone="amber" />
          <StatTile label="Complained" value={s.complained} icon={AlertCircle} tone="rose" />
          <StatTile label="Pending" value={s.pending} icon={Loader2} tone="purple" />
        </div>
      </div>
      <div>
        <h2 className="text-sm font-semibold text-slate-700 mb-2">Campaigns</h2>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
          <StatTile label="Total" value={c.total} icon={Send} tone="blue" />
          <StatTile label="Sending" value={c.sending} icon={Loader2} tone="cyan" />
          <StatTile label="Delivered" value={c.sent} icon={CheckCircle2} tone="emerald" />
          <StatTile label="Failed" value={c.failed} icon={AlertCircle} tone="rose" />
          <StatTile label="Opens" value={c.opens} icon={Eye} tone="purple" />
          <StatTile label="Clicks" value={c.clicks} icon={MousePointerClick} tone="amber" />
        </div>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-semibold text-slate-800">Recent Campaigns</h3>
            <button onClick={() => onNavigate("campaigns")} className="text-xs font-medium text-primary hover:underline">View all</button>
          </div>
          {data.recentCampaigns.length === 0 ? <p className="text-sm text-slate-500 py-6 text-center">No campaigns yet</p> : (
            <div className="space-y-2">
              {data.recentCampaigns.map((c: Campaign) => (
                <div key={c.id} className="flex items-center justify-between p-2.5 rounded-lg hover:bg-slate-50">
                  <div className="min-w-0"><div className="font-medium text-sm text-slate-800 truncate">{c.name}</div><div className="text-xs text-slate-500">{c.sentCount} sent · {c.opensCount} opens · {c.clicksCount} clicks</div></div>
                  <Badge tone={c.status}>{c.status}</Badge>
                </div>
              ))}
            </div>
          )}
        </div>
        <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-semibold text-slate-800">New Subscribers</h3>
            <button onClick={() => onNavigate("subscribers")} className="text-xs font-medium text-primary hover:underline">View all</button>
          </div>
          {data.recentSubs.length === 0 ? <p className="text-sm text-slate-500 py-6 text-center">No subscribers yet</p> : (
            <div className="space-y-2">
              {data.recentSubs.map((s: Sub) => (
                <div key={s.id} className="flex items-center justify-between p-2.5 rounded-lg hover:bg-slate-50">
                  <div className="min-w-0"><div className="font-medium text-sm text-slate-800 truncate">{[s.firstName, s.lastName].filter(Boolean).join(" ") || s.email}</div><div className="text-xs text-slate-500 truncate">{s.email}</div></div>
                  <Badge tone={s.status}>{s.status}</Badge>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ---------------- Subscribers ----------------
function SubscribersView({ notify }: { notify: (m: string, k?: "ok" | "err") => void }) {
  const [rows, setRows] = useState<Sub[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const pageSize = 50;
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("all");
  const [group, setGroup] = useState("all");
  const [groups, setGroups] = useState<Group[]>([]);
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [loading, setLoading] = useState(true);
  const [addOpen, setAddOpen] = useState(false);
  const [editing, setEditing] = useState<Sub | null>(null);
  const [importOpen, setImportOpen] = useState(false);
  const [groupsOpen, setGroupsOpen] = useState(false);
  const [deleteGroupOpen, setDeleteGroupOpen] = useState(false);
  const [selectAllMatching, setSelectAllMatching] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: String(page), pageSize: String(pageSize), q, status, group });
      const data = await api(`/admin/newsletter/subscribers?${params}`);
      setRows(data.rows); setTotal(data.total); setSelected(new Set()); setSelectAllMatching(false);
    } catch (e: any) { notify(e.message, "err"); } finally { setLoading(false); }
  };
  const loadGroups = async () => { try { setGroups(await api("/admin/newsletter/groups")); } catch {} };
  useEffect(() => { load(); }, [page, status, group]);
  useEffect(() => { loadGroups(); }, []);
  useEffect(() => { const t = setTimeout(() => { setPage(1); load(); }, 300); return () => clearTimeout(t); }, [q]);

  const toggleAll = () => { if (selected.size === rows.length) setSelected(new Set()); else setSelected(new Set(rows.map(r => r.id))); };
  const toggle = (id: number) => { const s = new Set(selected); s.has(id) ? s.delete(id) : s.add(id); setSelected(s); };

  const bulkDelete = async () => {
    if (selectAllMatching) {
      if (!confirm(`Delete ALL ${total} matching subscribers? This cannot be undone.`)) return;
      try {
        const allIds: number[] = [];
        let p = 1; const ps = 500;
        while (true) {
          const params = new URLSearchParams({ page: String(p), pageSize: String(ps), q, status, group });
          const data = await api(`/admin/newsletter/subscribers?${params}`);
          allIds.push(...data.rows.map((r: Sub) => r.id));
          if (allIds.length >= data.total || data.rows.length === 0) break;
          p++;
        }
        await api("/admin/newsletter/subscribers/bulk-delete", { method: "POST", body: JSON.stringify({ ids: allIds }) });
        notify(`Deleted ${allIds.length} subscribers`); load();
      } catch (e: any) { notify(e.message, "err"); }
      return;
    }
    if (!selected.size || !confirm(`Delete ${selected.size} subscriber(s)?`)) return;
    try { await api("/admin/newsletter/subscribers/bulk-delete", { method: "POST", body: JSON.stringify({ ids: Array.from(selected) }) }); notify("Subscribers deleted"); load(); } catch (e: any) { notify(e.message, "err"); }
  };
  const exportCsv = () => { window.open(`${API}/admin/newsletter/subscribers/export.csv`, "_blank"); };

  return (
    <div className="space-y-3">
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-3">
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input value={q} onChange={e => setQ(e.target.value)} placeholder="Search email or name…" className="w-full pl-9 pr-3 py-2 text-sm rounded-lg border border-slate-200 focus:border-primary focus:outline-none" />
          </div>
          <select value={status} onChange={e => { setStatus(e.target.value); setPage(1); }} className="px-3 py-2 text-sm rounded-lg border border-slate-200 bg-white">
            <option value="all">All statuses</option>
            <option value="subscribed">Subscribed</option><option value="unsubscribed">Unsubscribed</option>
            <option value="bounced">Bounced</option><option value="complained">Complained</option><option value="pending">Pending</option>
          </select>
          <select value={group} onChange={e => { setGroup(e.target.value); setPage(1); }} className="px-3 py-2 text-sm rounded-lg border border-slate-200 bg-white">
            <option value="all">All groups</option>
            {groups.map(g => <option key={g.id} value={g.name}>{g.name}</option>)}
          </select>
          <button onClick={() => setGroupsOpen(true)} className="px-3 py-2 text-sm rounded-lg border border-slate-200 hover:bg-slate-50">Manage Groups</button>
          <button onClick={exportCsv} className="px-3 py-2 text-sm rounded-lg border border-slate-200 hover:bg-slate-50 flex items-center gap-1.5"><Download className="w-4 h-4" /> Export</button>
          <button onClick={() => setImportOpen(true)} className="px-3 py-2 text-sm rounded-lg border border-slate-200 hover:bg-slate-50 flex items-center gap-1.5"><Upload className="w-4 h-4" /> Import</button>
          <button onClick={() => setDeleteGroupOpen(true)} className="px-3 py-2 text-sm rounded-lg border border-rose-200 text-rose-600 hover:bg-rose-50 flex items-center gap-1.5"><Trash2 className="w-4 h-4" /> Delete by group</button>
          <button onClick={() => setAddOpen(true)} className="px-3 py-2 text-sm rounded-lg bg-primary text-white hover:bg-primary/90 flex items-center gap-1.5"><Plus className="w-4 h-4" /> Add</button>
        </div>
        {selected.size > 0 && (
          <div className="mt-3 flex items-center gap-3 bg-blue-50 rounded-lg p-2.5 text-sm">
            <span className="font-medium text-blue-800">{selected.size} selected{selectAllMatching ? ` (all ${total} matching)` : ""}</span>
            {!selectAllMatching && rows.length > 0 && selected.size === rows.length && total > rows.length && (
              <button onClick={() => setSelectAllMatching(true)} className="text-xs font-medium text-blue-700 underline hover:text-blue-900">Select all {total} matching subscribers</button>
            )}
            {selectAllMatching && (
              <button onClick={() => { setSelectAllMatching(false); setSelected(new Set()); }} className="text-xs font-medium text-blue-700 underline hover:text-blue-900">Clear selection</button>
            )}
            <button onClick={bulkDelete} className="px-3 py-1 rounded bg-rose-600 text-white text-xs hover:bg-rose-700 flex items-center gap-1"><Trash2 className="w-3.5 h-3.5" /> Delete</button>
          </div>
        )}
      </div>

      <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
        {loading ? <div className="p-12 text-center text-slate-500"><Loader2 className="w-6 h-6 animate-spin inline" /></div> : rows.length === 0 ? (
          <div className="p-12 text-center text-slate-500">No subscribers</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-600 text-xs uppercase tracking-wider">
                <tr>
                  <th className="p-3 text-left w-10"><input type="checkbox" checked={selected.size === rows.length && rows.length > 0} onChange={toggleAll} /></th>
                  <th className="p-3 text-left">Email</th>
                  <th className="p-3 text-left">Name</th>
                  <th className="p-3 text-left">Status</th>
                  <th className="p-3 text-left">Groups</th>
                  <th className="p-3 text-left">Source</th>
                  <th className="p-3 text-left">Added</th>
                  <th className="p-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {rows.map(r => (
                  <tr key={r.id} className="border-t border-slate-100 hover:bg-slate-50">
                    <td className="p-3"><input type="checkbox" checked={selected.has(r.id)} onChange={() => toggle(r.id)} /></td>
                    <td className="p-3 font-medium text-slate-800">{r.email}</td>
                    <td className="p-3 text-slate-600">{[r.firstName, r.lastName].filter(Boolean).join(" ") || "—"}</td>
                    <td className="p-3"><Badge tone={r.status}>{r.status}</Badge></td>
                    <td className="p-3 text-xs text-slate-500">{(r.groups || []).join(", ") || "—"}</td>
                    <td className="p-3 text-xs text-slate-500">{r.source || "—"}</td>
                    <td className="p-3 text-xs text-slate-500">{new Date(r.createdAt).toLocaleDateString()}</td>
                    <td className="p-3 text-right">
                      <button onClick={() => setEditing(r)} className="p-1.5 rounded hover:bg-slate-100 text-slate-500" title="Edit"><Pencil className="w-4 h-4" /></button>
                      <button onClick={async () => { if (!confirm("Delete subscriber?")) return; try { await api(`/admin/newsletter/subscribers/${r.id}`, { method: "DELETE" }); notify("Deleted"); load(); } catch (e: any) { notify(e.message, "err"); } }}
                        className="p-1.5 rounded hover:bg-rose-50 text-rose-500" title="Delete"><Trash2 className="w-4 h-4" /></button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {total > pageSize && (
          <div className="px-4 py-3 border-t border-slate-100 flex items-center justify-between text-sm">
            <div className="text-slate-500">Showing {(page - 1) * pageSize + 1}–{Math.min(page * pageSize, total)} of {total}</div>
            <div className="flex gap-1">
              <button disabled={page <= 1} onClick={() => setPage(p => p - 1)} className="px-3 py-1 rounded border border-slate-200 disabled:opacity-50">Prev</button>
              <button disabled={page * pageSize >= total} onClick={() => setPage(p => p + 1)} className="px-3 py-1 rounded border border-slate-200 disabled:opacity-50">Next</button>
            </div>
          </div>
        )}
      </div>

      {(addOpen || editing) && (
        <SubscriberEditModal onClose={() => { setAddOpen(false); setEditing(null); }} sub={editing} groups={groups}
          onSaved={() => { setAddOpen(false); setEditing(null); load(); notify("Saved"); }} notify={notify} />
      )}
      {importOpen && <ImportCsvModal onClose={() => setImportOpen(false)} groups={groups} onDone={(imp, skp) => { setImportOpen(false); notify(`Imported ${imp}, skipped ${skp}`); load(); }} notify={notify} />}
      {groupsOpen && <GroupsModal onClose={() => setGroupsOpen(false)} notify={notify} onChanged={loadGroups} groups={groups} />}
      {deleteGroupOpen && <DeleteByGroupModal onClose={() => setDeleteGroupOpen(false)} groups={groups} onDone={(n, gname) => { setDeleteGroupOpen(false); notify(`Deleted ${n} subscribers from "${gname}"`); load(); }} notify={notify} />}
    </div>
  );
}

function SubscriberEditModal({ sub, groups, onClose, onSaved, notify }: { sub: Sub | null; groups: Group[]; onClose: () => void; onSaved: () => void; notify: (m: string, k?: "ok" | "err") => void }) {
  const [email, setEmail] = useState(sub?.email || "");
  const [firstName, setFirstName] = useState(sub?.firstName || "");
  const [lastName, setLastName] = useState(sub?.lastName || "");
  const [status, setStatus] = useState(sub?.status || "subscribed");
  const [grp, setGrp] = useState<string[]>(sub?.groups || []);
  const [saving, setSaving] = useState(false);
  const save = async () => {
    setSaving(true);
    try {
      if (sub) await api(`/admin/newsletter/subscribers/${sub.id}`, { method: "PATCH", body: JSON.stringify({ email, firstName, lastName, status, groups: grp }) });
      else await api("/admin/newsletter/subscribers", { method: "POST", body: JSON.stringify({ email, firstName, lastName, status, groups: grp }) });
      onSaved();
    } catch (e: any) { notify(e.message, "err"); } finally { setSaving(false); }
  };
  const toggleGrp = (n: string) => setGrp(g => g.includes(n) ? g.filter(x => x !== n) : [...g, n]);
  return (
    <Modal open onClose={onClose} title={sub ? "Edit Subscriber" : "Add Subscriber"}>
      <div className="space-y-3">
        <Field label="Email *"><input value={email} onChange={e => setEmail(e.target.value)} className="input" /></Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="First name"><input value={firstName} onChange={e => setFirstName(e.target.value)} className="input" /></Field>
          <Field label="Last name"><input value={lastName} onChange={e => setLastName(e.target.value)} className="input" /></Field>
        </div>
        <Field label="Status">
          <select value={status} onChange={e => setStatus(e.target.value)} className="input">
            <option value="subscribed">Subscribed</option><option value="unsubscribed">Unsubscribed</option>
            <option value="bounced">Bounced</option><option value="complained">Complained</option><option value="pending">Pending</option>
          </select>
        </Field>
        {groups.length > 0 && (
          <Field label="Groups">
            <div className="flex flex-wrap gap-2">
              {groups.map(g => <button key={g.id} type="button" onClick={() => toggleGrp(g.name)}
                className={`px-3 py-1 rounded-full text-xs font-medium border ${grp.includes(g.name) ? "bg-primary text-white border-primary" : "bg-white border-slate-200 text-slate-600"}`}>{g.name}</button>)}
            </div>
          </Field>
        )}
        <div className="flex justify-end gap-2 pt-2">
          <button onClick={onClose} className="px-4 py-2 rounded-lg border border-slate-200 text-sm">Cancel</button>
          <button disabled={saving || !email} onClick={save} className="px-4 py-2 rounded-lg bg-primary text-white text-sm disabled:opacity-50">{saving ? "Saving…" : "Save"}</button>
        </div>
      </div>
    </Modal>
  );
}

function GroupsModal({ onClose, notify, onChanged, groups }: { onClose: () => void; notify: (m: string, k?: "ok" | "err") => void; onChanged: () => void; groups: Group[] }) {
  const [name, setName] = useState("");
  const [list, setList] = useState(groups);
  useEffect(() => { setList(groups); }, [groups]);
  const refresh = async () => { try { const g = await api("/admin/newsletter/groups"); setList(g); onChanged(); } catch {} };
  const add = async () => { if (!name.trim()) return; try { await api("/admin/newsletter/groups", { method: "POST", body: JSON.stringify({ name: name.trim() }) }); setName(""); refresh(); notify("Group added"); } catch (e: any) { notify(e.message, "err"); } };
  const del = async (id: number) => { if (!confirm("Delete group?")) return; try { await api(`/admin/newsletter/groups/${id}`, { method: "DELETE" }); refresh(); } catch (e: any) { notify(e.message, "err"); } };
  return (
    <Modal open onClose={onClose} title="Manage Groups">
      <div className="space-y-3">
        <div className="flex gap-2"><input value={name} onChange={e => setName(e.target.value)} placeholder="New group name" className="input flex-1" /><button onClick={add} className="px-4 py-2 rounded-lg bg-primary text-white text-sm">Add</button></div>
        {list.length === 0 ? <p className="text-sm text-slate-500 py-4 text-center">No groups yet</p> : (
          <div className="space-y-1">
            {list.map(g => (
              <div key={g.id} className="flex items-center justify-between p-2.5 rounded-lg border border-slate-200">
                <span className="text-sm font-medium">{g.name}</span>
                <button onClick={() => del(g.id)} className="p-1 rounded hover:bg-rose-50 text-rose-500"><Trash2 className="w-4 h-4" /></button>
              </div>
            ))}
          </div>
        )}
      </div>
    </Modal>
  );
}

function ImportCsvModal({ onClose, groups, onDone, notify }: { onClose: () => void; groups: Group[]; onDone: (i: number, s: number) => void; notify: (m: string, k?: "ok" | "err") => void }) {
  const [csv, setCsv] = useState("");
  const [xlsxRows, setXlsxRows] = useState<{ email: string; firstName?: string | null; lastName?: string | null }[] | null>(null);
  const [fileName, setFileName] = useState("");
  const [grp, setGrp] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);

  const onFile = async (f: File) => {
    setFileName(f.name);
    const name = f.name.toLowerCase();
    if (name.endsWith(".xlsx")) {
      try {
        const readXlsxFile = (await import("read-excel-file")).default;
        const allRows = await readXlsxFile(f);
        if (allRows.length < 2) { notify("Spreadsheet is empty", "err"); setXlsxRows(null); return; }
        const headers = allRows[0].map(h => String(h ?? ""));
        const dataRows = allRows.slice(1);
        const findKey = (re: RegExp) => headers.find(h => re.test(h.toLowerCase()));
        const emailKey = findKey(/^e-?mail$/) || findKey(/email/);
        const firstKey = findKey(/first.?name|fname|first/);
        const lastKey = findKey(/last.?name|lname|surname|last/);
        if (!emailKey) { notify("Spreadsheet must include an 'email' column", "err"); setXlsxRows(null); return; }
        const emailIdx = headers.indexOf(emailKey);
        const firstIdx = firstKey ? headers.indexOf(firstKey) : -1;
        const lastIdx = lastKey ? headers.indexOf(lastKey) : -1;
        const rows = dataRows.map(row => ({
          email: String(row[emailIdx] ?? "").trim().toLowerCase(),
          firstName: firstIdx >= 0 ? String(row[firstIdx] ?? "").trim() : null,
          lastName: lastIdx >= 0 ? String(row[lastIdx] ?? "").trim() : null,
        })).filter(r => r.email);
        setXlsxRows(rows);
        setCsv("");
      } catch (e: any) { notify(`Could not read spreadsheet: ${e.message}`, "err"); setXlsxRows(null); }
    } else {
      const r = new FileReader();
      r.onload = () => { setCsv(String(r.result || "")); setXlsxRows(null); };
      r.readAsText(f);
    }
  };

  const submit = async () => {
    if (!csv.trim() && !(xlsxRows && xlsxRows.length)) { notify("Provide a file or paste data", "err"); return; }
    setBusy(true);
    try {
      const body = xlsxRows && xlsxRows.length
        ? JSON.stringify({ rows: xlsxRows, groups: grp, source: "xlsx-import" })
        : JSON.stringify({ csv, groups: grp });
      const r = await api("/admin/newsletter/subscribers/import", { method: "POST", body });
      onDone(r.imported, r.skipped);
    } catch (e: any) { notify(e.message, "err"); }
    finally { setBusy(false); }
  };

  return (
    <Modal open onClose={onClose} title="Import Subscribers" size="lg">
      <div className="space-y-3">
        <p className="text-xs text-slate-500">
          Upload a <strong>.csv</strong> or <strong>.xlsx</strong> file (or paste CSV below). First row must be headers including an <code className="px-1 bg-slate-100 rounded">email</code> column. Optional: <code className="px-1 bg-slate-100 rounded">first_name</code>, <code className="px-1 bg-slate-100 rounded">last_name</code>. Duplicates and invalid emails are skipped.
        </p>
        <div className="flex items-center gap-3">
          <input type="file" accept=".csv,.xlsx,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" onChange={e => e.target.files?.[0] && onFile(e.target.files[0])} className="text-sm" />
          {fileName && <span className="text-xs text-slate-500">{fileName}</span>}
        </div>
        {xlsxRows ? (
          <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-800">
            <div className="font-medium">Spreadsheet parsed: {xlsxRows.length} row{xlsxRows.length === 1 ? "" : "s"} ready to import.</div>
            <div className="text-xs mt-1 text-emerald-700">Preview: {xlsxRows.slice(0, 3).map(r => r.email).join(", ")}{xlsxRows.length > 3 ? "…" : ""}</div>
          </div>
        ) : (
          <textarea value={csv} onChange={e => { setCsv(e.target.value); setXlsxRows(null); }} rows={10} placeholder="email,first_name,last_name&#10;john@example.com,John,Doe" className="input font-mono text-xs" />
        )}
        {groups.length > 0 && (
          <Field label="Add imported subscribers to groups (optional)">
            <div className="flex flex-wrap gap-2">
              {groups.map(g => <button key={g.id} type="button" onClick={() => setGrp(s => s.includes(g.name) ? s.filter(x => x !== g.name) : [...s, g.name])}
                className={`px-3 py-1 rounded-full text-xs font-medium border ${grp.includes(g.name) ? "bg-primary text-white border-primary" : "bg-white border-slate-200 text-slate-600"}`}>{g.name}</button>)}
            </div>
          </Field>
        )}
        <div className="flex justify-end gap-2"><button onClick={onClose} className="px-4 py-2 rounded-lg border border-slate-200 text-sm">Cancel</button><button disabled={busy} onClick={submit} className="px-4 py-2 rounded-lg bg-primary text-white text-sm disabled:opacity-50">{busy ? "Importing…" : "Import"}</button></div>
      </div>
    </Modal>
  );
}

function DeleteByGroupModal({ onClose, groups, onDone, notify }: { onClose: () => void; groups: Group[]; onDone: (n: number, group: string) => void; notify: (m: string, k?: "ok" | "err") => void }) {
  const [group, setGroup] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!group) { notify("Select a group", "err"); return; }
    if (!confirm(`Permanently delete every subscriber in "${group}"? This cannot be undone.`)) return;
    setBusy(true);
    try {
      const r = await api("/admin/newsletter/subscribers/delete-by-group", { method: "POST", body: JSON.stringify({ group }) });
      onDone(r.deleted || 0, group);
    } catch (e: any) { notify(e.message, "err"); }
    finally { setBusy(false); }
  };
  return (
    <Modal open onClose={onClose} title="Delete Subscribers by Group">
      <div className="space-y-3">
        <p className="text-sm text-slate-600">This will permanently remove every subscriber assigned to the selected group. Subscribers in other groups are unaffected.</p>
        <Field label="Group *">
          <select value={group} onChange={e => setGroup(e.target.value)} className="input">
            <option value="">— Select a group —</option>
            {groups.map(g => <option key={g.id} value={g.name}>{g.name}</option>)}
          </select>
        </Field>
        {groups.length === 0 && <p className="text-xs text-amber-700 bg-amber-50 rounded-lg p-2.5">You have no groups yet. Create one with "Manage Groups".</p>}
        <div className="flex justify-end gap-2 pt-2">
          <button onClick={onClose} className="px-4 py-2 rounded-lg border border-slate-200 text-sm">Cancel</button>
          <button disabled={busy || !group} onClick={submit} className="px-4 py-2 rounded-lg bg-rose-600 text-white text-sm disabled:opacity-50 flex items-center gap-1.5"><Trash2 className="w-4 h-4" />{busy ? "Deleting…" : "Delete all in group"}</button>
        </div>
      </div>
    </Modal>
  );
}

// ---------------- Templates ----------------
function TemplatesView({ notify }: { notify: (m: string, k?: "ok" | "err") => void }) {
  const [rows, setRows] = useState<Template[]>([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState<Template | null>(null);
  const [creating, setCreating] = useState(false);
  const load = async () => { setLoading(true); try { setRows(await api("/admin/newsletter/templates")); } catch (e: any) { notify(e.message, "err"); } finally { setLoading(false); } };
  useEffect(() => { load(); }, []);

  if (editing) return <TemplateEditor template={editing} onClose={() => { setEditing(null); load(); }} notify={notify} />;
  if (creating) return <TemplateEditor template={null} onClose={() => { setCreating(false); load(); }} notify={notify} />;

  return (
    <div className="space-y-3">
      <div className="flex justify-between items-center bg-white p-3 rounded-xl border border-slate-200 shadow-sm">
        <h2 className="font-semibold text-slate-800">Email Templates</h2>
        <button onClick={() => setCreating(true)} className="px-3 py-2 text-sm rounded-lg bg-primary text-white flex items-center gap-1.5"><Plus className="w-4 h-4" /> New Template</button>
      </div>
      {loading ? <div className="p-12 text-center"><Loader2 className="w-6 h-6 animate-spin inline text-slate-500" /></div> : rows.length === 0 ? (
        <div className="p-12 text-center text-slate-500 bg-white rounded-xl border border-slate-200">No templates yet. Create one to get started.</div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {rows.map(t => (
            <div key={t.id} className="bg-white rounded-xl border border-slate-200 shadow-sm p-4 hover:shadow-md transition-shadow">
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <h3 className="font-semibold text-slate-800 truncate">{t.name}</h3>
                  <p className="text-xs text-slate-500 truncate mt-0.5">Subject: {t.subject}</p>
                </div>
                <Badge tone="subscribed">{t.isPlainText ? "Text" : "Builder"}</Badge>
              </div>
              <div className="text-xs text-slate-400 mt-2">Updated {new Date(t.updatedAt).toLocaleDateString()}</div>
              <div className="flex gap-2 mt-3 pt-3 border-t border-slate-100">
                <button onClick={() => setEditing(t)} className="flex-1 px-3 py-1.5 text-xs font-medium rounded-lg bg-slate-100 hover:bg-slate-200 flex items-center justify-center gap-1"><Pencil className="w-3.5 h-3.5" /> Edit</button>
                <button onClick={async () => { try { const r = await api(`/admin/newsletter/templates/${t.id}/duplicate`, { method: "POST" }); notify("Duplicated"); load(); } catch (e: any) { notify(e.message, "err"); } }} className="px-2 py-1.5 text-xs rounded-lg bg-slate-100 hover:bg-slate-200" title="Duplicate"><Copy className="w-3.5 h-3.5" /></button>
                <button onClick={async () => { if (!confirm("Delete template?")) return; try { await api(`/admin/newsletter/templates/${t.id}`, { method: "DELETE" }); notify("Deleted"); load(); } catch (e: any) { notify(e.message, "err"); } }} className="px-2 py-1.5 text-xs rounded-lg bg-rose-50 hover:bg-rose-100 text-rose-600"><Trash2 className="w-3.5 h-3.5" /></button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function uid() { return Math.random().toString(36).slice(2, 10); }

function TemplateEditor({ template, onClose, notify }: { template: Template | null; onClose: () => void; notify: (m: string, k?: "ok" | "err") => void }) {
  const [name, setName] = useState(template?.name || "");
  const [subject, setSubject] = useState(template?.subject || "");
  const [preheader, setPreheader] = useState(template?.preheader || "");
  const [isPlainText, setIsPlainText] = useState(template?.isPlainText || false);
  const [bodyText, setBodyText] = useState(template?.bodyText || "");
  const [signatureHtml, setSignatureHtml] = useState(template?.signatureHtml || "");
  const [blocks, setBlocks] = useState<any[]>(template?.blocks || []);
  const [saving, setSaving] = useState(false);
  const [previewHtml, setPreviewHtml] = useState<string | null>(null);
  const [testTo, setTestTo] = useState("");
  const [testOpen, setTestOpen] = useState(false);
  const [spamOpen, setSpamOpen] = useState(false);
  const [id, setId] = useState(template?.id);

  const save = async (silent = false): Promise<number | undefined> => {
    setSaving(true);
    try {
      const payload = { name: name || "Untitled", subject, preheader, isPlainText, bodyText, signatureHtml, blocks };
      if (id) { await api(`/admin/newsletter/templates/${id}`, { method: "PATCH", body: JSON.stringify(payload) }); if (!silent) notify("Template saved"); return id; }
      const r = await api("/admin/newsletter/templates", { method: "POST", body: JSON.stringify(payload) });
      setId(r.id);
      if (!silent) notify("Template saved");
      return r.id;
    } catch (e: any) { notify(e.message, "err"); return undefined; } finally { setSaving(false); }
  };
  const preview = async () => {
    const tid = await save(true);
    if (!tid) { notify("Save first", "err"); return; }
    try { const r = await api(`/admin/newsletter/templates/${tid}/preview`, { method: "POST" }); setPreviewHtml(r.html); } catch (e: any) { notify(e.message, "err"); }
  };
  const sendTest = async () => {
    const tid = await save(true);
    if (!tid) { notify("Save first", "err"); return; }
    try { await api(`/admin/newsletter/templates/${tid}/test-send`, { method: "POST", body: JSON.stringify({ to: testTo }) }); notify("Test sent"); setTestOpen(false); } catch (e: any) { notify(e.message, "err"); }
  };

  const addBlock = (type: string) => {
    const base: any = { id: uid(), type };
    if (type === "heading") { base.text = "Your heading"; base.level = 2; base.align = "left"; }
    if (type === "paragraph") base.text = "Write your content here. Use {{firstName}} for personalization.";
    if (type === "image") { base.url = ""; base.alt = ""; }
    if (type === "button") { base.label = "Click here"; base.href = "https://"; base.align = "center"; }
    if (type === "spacer") base.height = 24;
    setBlocks(b => [...b, base]);
  };
  const moveBlock = (idx: number, dir: -1 | 1) => { const next = [...blocks]; const target = idx + dir; if (target < 0 || target >= next.length) return; [next[idx], next[target]] = [next[target], next[idx]]; setBlocks(next); };
  const removeBlock = (idx: number) => setBlocks(b => b.filter((_, i) => i !== idx));
  const updateBlock = (idx: number, patch: any) => setBlocks(b => b.map((x, i) => i === idx ? { ...x, ...patch } : x));

  return (
    <div className="space-y-3">
      <div className="bg-white p-3 rounded-xl border border-slate-200 shadow-sm flex items-center gap-2 flex-wrap">
        <button onClick={onClose} className="p-1.5 rounded hover:bg-slate-100"><ArrowLeft className="w-4 h-4" /></button>
        <input value={name} onChange={e => setName(e.target.value)} placeholder="Template name" className="flex-1 min-w-[160px] px-3 py-2 text-sm font-semibold rounded-lg border border-slate-200" />
        <button onClick={preview} className="px-3 py-2 text-sm rounded-lg border border-slate-200 hover:bg-slate-50 flex items-center gap-1.5"><Eye className="w-4 h-4" /> Preview</button>
        <button onClick={() => setSpamOpen(true)} className="px-3 py-2 text-sm rounded-lg border border-slate-200 hover:bg-slate-50 flex items-center gap-1.5"><ShieldCheck className="w-4 h-4" /> Spam check</button>
        <button onClick={() => setTestOpen(true)} className="px-3 py-2 text-sm rounded-lg border border-slate-200 hover:bg-slate-50 flex items-center gap-1.5"><Send className="w-4 h-4" /> Test send</button>
        <button onClick={() => save()} disabled={saving} className="px-3 py-2 text-sm rounded-lg bg-primary text-white disabled:opacity-50">{saving ? "Saving…" : "Save"}</button>
      </div>

      <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm space-y-3">
        <Field label="Subject"><input value={subject} onChange={e => setSubject(e.target.value)} className="input" placeholder="Hello {{firstName}}, your update is here" /></Field>
        <Field label="Preheader (preview text)"><input value={preheader} onChange={e => setPreheader(e.target.value)} className="input" /></Field>
        <div className="flex items-center gap-2">
          <button onClick={() => setIsPlainText(false)} className={`px-3 py-1.5 text-xs rounded-lg ${!isPlainText ? "bg-primary text-white" : "bg-slate-100 text-slate-600"}`}>Builder</button>
          <button onClick={() => setIsPlainText(true)} className={`px-3 py-1.5 text-xs rounded-lg ${isPlainText ? "bg-primary text-white" : "bg-slate-100 text-slate-600"}`}>Plain text</button>
        </div>
      </div>

      {isPlainText ? (
        <>
          <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm">
            <div className="block">
              <span className="text-xs font-medium text-slate-600 mb-1 block">Message body</span>
              <RichTextEditor value={bodyText} onChange={setBodyText} />
              <p className="text-xs text-slate-500 mt-2">Tokens: <code className="bg-slate-100 px-1 rounded">{`{{firstName}}`}</code>, <code className="bg-slate-100 px-1 rounded">{`{{lastName}}`}</code>, <code className="bg-slate-100 px-1 rounded">{`{{email}}`}</code></p>
            </div>
          </div>
          <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm">
            <div className="block">
              <span className="text-xs font-medium text-slate-600 mb-1 block">Signature</span>
              <RichTextEditor value={signatureHtml} onChange={setSignatureHtml} minRows={6} />
              <p className="text-xs text-slate-500 mt-2">Paste your signature directly from Outlook, Gmail or any email client — formatting, images and links are preserved. Appears below the message body in every email.</p>
            </div>
          </div>
        </>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-[260px_1fr] gap-3">
          <div className="bg-white p-3 rounded-xl border border-slate-200 shadow-sm h-fit lg:sticky lg:top-4">
            <h3 className="text-xs font-semibold text-slate-500 uppercase mb-2">Add Block</h3>
            <div className="space-y-1.5">
              <BlockBtn icon={Heading1} label="Heading" onClick={() => addBlock("heading")} />
              <BlockBtn icon={Type} label="Paragraph" onClick={() => addBlock("paragraph")} />
              <BlockBtn icon={ImageIcon} label="Image" onClick={() => addBlock("image")} />
              <BlockBtn icon={Link2} label="Button" onClick={() => addBlock("button")} />
              <BlockBtn icon={Minus} label="Divider" onClick={() => addBlock("divider")} />
              <BlockBtn icon={MoveVertical} label="Spacer" onClick={() => addBlock("spacer")} />
            </div>
            <p className="text-xs text-slate-500 mt-3">Tokens: <code className="bg-slate-100 px-1 rounded">{`{{firstName}}`}</code>, <code className="bg-slate-100 px-1 rounded">{`{{lastName}}`}</code>, <code className="bg-slate-100 px-1 rounded">{`{{email}}`}</code></p>
          </div>
          <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm min-h-[300px]">
            {blocks.length === 0 ? <div className="text-center py-16 text-slate-400 text-sm">Add blocks from the left to start building.</div> : (
              <div className="space-y-2">
                {blocks.map((b, idx) => (
                  <BlockEditor key={b.id} block={b} onChange={(p: any) => updateBlock(idx, p)} onMoveUp={() => moveBlock(idx, -1)} onMoveDown={() => moveBlock(idx, 1)} onRemove={() => removeBlock(idx)} canUp={idx > 0} canDown={idx < blocks.length - 1} />
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {previewHtml !== null && (
        <Modal open onClose={() => setPreviewHtml(null)} title="Preview" size="xl">
          <iframe srcDoc={previewHtml} className="w-full h-[70vh] border border-slate-200 rounded-lg" />
        </Modal>
      )}
      {testOpen && (
        <Modal open onClose={() => setTestOpen(false)} title="Send test email" size="sm">
          <div className="space-y-3">
            <Field label="To email"><input value={testTo} onChange={e => setTestTo(e.target.value)} className="input" placeholder="you@example.com" /></Field>
            <div className="flex justify-end gap-2"><button onClick={() => setTestOpen(false)} className="px-4 py-2 rounded-lg border border-slate-200 text-sm">Cancel</button><button onClick={sendTest} disabled={!testTo} className="px-4 py-2 rounded-lg bg-primary text-white text-sm disabled:opacity-50">Send</button></div>
          </div>
        </Modal>
      )}
      {spamOpen && (
        <Modal open onClose={() => setSpamOpen(false)} title="Spam score & recommendations" size="lg">
          <SpamReport subject={subject} preheader={preheader} bodyHtml={isPlainText ? bodyText : ""} blocks={isPlainText ? [] : blocks} signatureHtml={signatureHtml} isPlainText={isPlainText} />
        </Modal>
      )}
    </div>
  );
}

type SpamCheck = { id: string; severity: "ok" | "warn" | "bad"; weight: number; title: string; detail: string };

function analyzeSpam(input: { subject: string; preheader: string; bodyHtml: string; blocks: any[]; signatureHtml: string; isPlainText: boolean }): { score: number; grade: "Excellent" | "Good" | "Fair" | "Poor"; checks: SpamCheck[] } {
  const checks: SpamCheck[] = [];
  const stripTags = (s: string) => s.replace(/<style[\s\S]*?<\/style>/gi, "").replace(/<script[\s\S]*?<\/script>/gi, "").replace(/<[^>]+>/g, " ").replace(/&nbsp;/g, " ").replace(/&[a-z#0-9]+;/gi, " ").replace(/\s+/g, " ").trim();
  const blocksText = (input.blocks || []).map((b: any) => b.text || b.label || "").join(" ");
  const bodyPlain = input.isPlainText ? stripTags(input.bodyHtml || "") : blocksText;
  const sigPlain = stripTags(input.signatureHtml || "");
  const fullText = `${input.subject} ${input.preheader} ${bodyPlain} ${sigPlain}`.toLowerCase();
  const fullHtml = `${input.bodyHtml || ""} ${(input.blocks || []).map((b: any) => JSON.stringify(b)).join(" ")} ${input.signatureHtml || ""}`;

  // 1. Subject length
  const subjLen = input.subject.trim().length;
  if (subjLen === 0) checks.push({ id: "subj-empty", severity: "bad", weight: 25, title: "Subject is empty", detail: "Every email needs a subject. Empty subjects are a strong spam signal and look broken in inboxes." });
  else if (subjLen < 20) checks.push({ id: "subj-short", severity: "warn", weight: 5, title: "Subject is very short", detail: `Your subject is ${subjLen} characters. Aim for 30–60 characters to give context and improve open rates.` });
  else if (subjLen > 70) checks.push({ id: "subj-long", severity: "warn", weight: 5, title: "Subject is too long", detail: `Your subject is ${subjLen} characters and will be truncated on mobile. Keep it under 60–70.` });
  else checks.push({ id: "subj-ok", severity: "ok", weight: 0, title: "Subject length looks good", detail: `${subjLen} characters — within the recommended 30–60 range.` });

  // 2. Subject in ALL CAPS
  const letters = input.subject.replace(/[^A-Za-z]/g, "");
  const caps = input.subject.replace(/[^A-Z]/g, "");
  if (letters.length > 6 && caps.length / letters.length > 0.5) checks.push({ id: "subj-caps", severity: "bad", weight: 15, title: "Subject uses excessive CAPS", detail: "More than half of the letters in your subject are uppercase. Spam filters and recipients both react badly. Use sentence case." });

  // 3. Subject punctuation
  if (/!{2,}|\?{2,}|!\?|\?!/.test(input.subject)) checks.push({ id: "subj-punct", severity: "bad", weight: 10, title: "Excessive punctuation in subject", detail: "Multiple !! or ?? is a classic spam pattern. Remove repeated punctuation." });
  else if ((input.subject.match(/!/g) || []).length >= 1 && subjLen < 50 && (input.subject.match(/!/g) || []).length >= 2) checks.push({ id: "subj-bangs", severity: "warn", weight: 5, title: "Too many exclamation marks", detail: "Try to use at most one exclamation mark in the subject." });

  // 4. Spam trigger words
  const spamWords = [
    "free", "100% free", "act now", "urgent", "winner", "congratulations", "guarantee", "guaranteed",
    "risk free", "no obligation", "click here", "buy now", "order now", "limited time", "limited offer",
    "while supplies last", "this won't last", "earn $", "make money", "extra cash", "cash bonus",
    "double your", "earn extra", "lowest price", "best price", "discount", "save big", "huge savings",
    "cheap", "cheapest", "lowest interest", "amazing", "incredible deal", "miracle", "weight loss",
    "viagra", "casino", "lottery", "lifetime", "no fees", "no investment", "no purchase necessary",
    "satisfaction guaranteed", "exclusive deal", "you have been selected", "claim now", "instant", "fast cash",
  ];
  const foundSpam = spamWords.filter(w => fullText.includes(w));
  if (foundSpam.length >= 5) checks.push({ id: "spam-words-high", severity: "bad", weight: 20, title: `${foundSpam.length} spam trigger words detected`, detail: `Found: ${foundSpam.slice(0, 8).join(", ")}${foundSpam.length > 8 ? "…" : ""}. Rewrite to sound natural and informative.` });
  else if (foundSpam.length >= 2) checks.push({ id: "spam-words-mid", severity: "warn", weight: 10, title: `${foundSpam.length} spam trigger words detected`, detail: `Found: ${foundSpam.join(", ")}. Reduce or reword.` });
  else if (foundSpam.length === 1) checks.push({ id: "spam-words-low", severity: "warn", weight: 3, title: "1 spam trigger word detected", detail: `"${foundSpam[0]}" is a known trigger. Consider rephrasing.` });

  // 5. Currency / money signals
  const moneyHits = (fullText.match(/\$\d|£\d|€\d|\$\$\$|free \$|cash !|!!!/g) || []).length;
  if (moneyHits >= 2) checks.push({ id: "money", severity: "warn", weight: 8, title: "Money-related symbols overused", detail: "Multiple currency-heavy phrases like '$$$' or 'FREE $' flag emails as promotional. Mention prices in plain prose." });

  // 6. ALL CAPS words in body
  const capsWords = (bodyPlain.match(/\b[A-Z]{4,}\b/g) || []).length;
  if (capsWords >= 5) checks.push({ id: "body-caps", severity: "bad", weight: 10, title: `${capsWords} ALL-CAPS words in body`, detail: "Use bold or italics for emphasis instead of shouting in capitals." });
  else if (capsWords >= 2) checks.push({ id: "body-caps-low", severity: "warn", weight: 4, title: `${capsWords} ALL-CAPS words in body`, detail: "Limit ALL CAPS to acronyms (e.g. 'HVAC', 'UK')." });

  // 7. Body length / content
  const wordCount = bodyPlain.split(/\s+/).filter(Boolean).length;
  if (wordCount === 0) checks.push({ id: "body-empty", severity: "bad", weight: 25, title: "Message body is empty", detail: "Write at least a few sentences. Empty bodies are auto-classified as spam." });
  else if (wordCount < 20) checks.push({ id: "body-thin", severity: "warn", weight: 10, title: "Very little text in body", detail: `Only ${wordCount} words. Aim for 50+ words so filters see real content (and so subscribers get value).` });

  // 8. Image-to-text ratio
  const imgCount = (fullHtml.match(/<img\b/gi) || []).length + (input.blocks || []).filter((b: any) => b.type === "image").length;
  if (imgCount > 0 && wordCount < imgCount * 20) checks.push({ id: "img-ratio", severity: "warn", weight: 8, title: "Too many images vs. text", detail: `You have ${imgCount} image${imgCount > 1 ? "s" : ""} but only ${wordCount} words. Image-heavy emails with little text trigger spam filters. Aim for at least 20 words per image.` });

  // 9. Missing image alt
  const imgsHtml = fullHtml.match(/<img[^>]*>/gi) || [];
  const imgsMissingAlt = imgsHtml.filter(t => !/\salt=/i.test(t) || /\salt=["']?["']/i.test(t)).length;
  if (imgsMissingAlt > 0) checks.push({ id: "img-alt", severity: "warn", weight: 4, title: `${imgsMissingAlt} image${imgsMissingAlt > 1 ? "s are" : " is"} missing alt text`, detail: "Add descriptive alt text so the email reads well when images are blocked (most Outlook & Gmail users)." });

  // 10. Link count + shorteners
  const links = (fullHtml.match(/<a\s[^>]*href=["']([^"']+)["']/gi) || []).map(m => (m.match(/href=["']([^"']+)["']/i) || [])[1] || "");
  const builderLinks = (input.blocks || []).filter((b: any) => b.type === "button" && b.href).map((b: any) => b.href as string);
  const allLinks = [...links, ...builderLinks];
  if (allLinks.length > 8) checks.push({ id: "links-many", severity: "warn", weight: 6, title: `${allLinks.length} links in this email`, detail: "Lots of links look spammy. Focus on 1–3 clear calls to action." });
  const shorteners = /\b(bit\.ly|tinyurl\.com|t\.co|goo\.gl|ow\.ly|buff\.ly|is\.gd|cutt\.ly)/i;
  if (allLinks.some(l => shorteners.test(l))) checks.push({ id: "links-short", severity: "bad", weight: 10, title: "Link shortener detected", detail: "Shortened URLs (bit.ly, tinyurl, etc.) heavily flag emails as spam. Use full vertexclimate.co.uk URLs." });

  // 11. Suspicious / hidden text styles
  if (/color\s*:\s*#?(fff|ffffff|white)/i.test(fullHtml) && /background[^;]*?(fff|ffffff|white)/i.test(fullHtml)) checks.push({ id: "hidden-text", severity: "bad", weight: 15, title: "Possible hidden white-on-white text", detail: "White text on a white background is interpreted as a deliberate spam-evasion trick. Remove it." });
  if (/font-size\s*:\s*[01](?:px|pt)/i.test(fullHtml)) checks.push({ id: "tiny-text", severity: "bad", weight: 15, title: "Tiny / invisible font detected", detail: "Font sizes of 0–1px hide content. Remove these styles." });

  // 12. Preheader
  if (!input.preheader.trim()) checks.push({ id: "preheader", severity: "warn", weight: 4, title: "Preheader is empty", detail: "Fill in a 40–100 character preheader. It's the second-most important factor in open rates after the subject." });

  // 13. Personalization
  if (!/\{\{firstName\}\}|\{\{lastName\}\}|\{\{email\}\}/i.test(`${input.subject} ${input.preheader} ${input.bodyHtml} ${blocksText}`)) checks.push({ id: "personalize", severity: "warn", weight: 3, title: "No personalization tokens used", detail: "Adding {{firstName}} to the subject or greeting typically improves opens by 10–25% and looks less like a blast." });

  // 14. Unsubscribe (we inject one automatically — inform user)
  checks.push({ id: "unsub", severity: "ok", weight: 0, title: "Unsubscribe link will be added automatically", detail: "Every campaign email includes a personal unsubscribe link in the footer — required by GDPR/CAN-SPAM." });

  // 15. Click-bait pattern in subject (RE:, FW:)
  if (/^(re|fwd?):/i.test(input.subject.trim())) checks.push({ id: "subj-fake-reply", severity: "bad", weight: 12, title: "Subject starts with 'Re:' or 'Fwd:'", detail: "Pretending an email is a reply or forward is a classic spam trick and damages trust. Remove the prefix." });

  // 16. Plain-text fallback length (for builder)
  if (!input.isPlainText && wordCount < 30 && imgCount > 0) checks.push({ id: "fallback", severity: "warn", weight: 5, title: "Weak plain-text fallback", detail: "Builder blocks generate the text fallback from block content. With so little text content, recipients without HTML rendering will see almost nothing." });

  // Score: start at 100, subtract weights of warn/bad
  const deduct = checks.filter(c => c.severity !== "ok").reduce((s, c) => s + c.weight, 0);
  const score = Math.max(0, Math.min(100, 100 - deduct));
  const grade = score >= 90 ? "Excellent" : score >= 75 ? "Good" : score >= 55 ? "Fair" : "Poor";
  // Sort: bad first, then warn, then ok
  const order = { bad: 0, warn: 1, ok: 2 } as const;
  checks.sort((a, b) => order[a.severity] - order[b.severity] || b.weight - a.weight);
  return { score, grade, checks };
}

function SpamReport(props: { subject: string; preheader: string; bodyHtml: string; blocks: any[]; signatureHtml: string; isPlainText: boolean }) {
  const result = useMemo(() => analyzeSpam(props), [props]);
  const { score, grade, checks } = result;
  const ringColor = score >= 90 ? "#10b981" : score >= 75 ? "#06b6d4" : score >= 55 ? "#f59e0b" : "#ef4444";
  const gradeColor = score >= 90 ? "text-emerald-600 bg-emerald-50 border-emerald-200" : score >= 75 ? "text-cyan-700 bg-cyan-50 border-cyan-200" : score >= 55 ? "text-amber-700 bg-amber-50 border-amber-200" : "text-red-700 bg-red-50 border-red-200";
  const issueCounts = { bad: checks.filter(c => c.severity === "bad").length, warn: checks.filter(c => c.severity === "warn").length, ok: checks.filter(c => c.severity === "ok").length };
  const circumference = 2 * Math.PI * 52;
  const dash = (score / 100) * circumference;
  return (
    <div className="space-y-4">
      <div className="flex items-center gap-5 p-4 bg-slate-50 rounded-xl border border-slate-200">
        <div className="relative w-32 h-32 flex-shrink-0">
          <svg viewBox="0 0 120 120" className="w-32 h-32 -rotate-90">
            <circle cx="60" cy="60" r="52" fill="none" stroke="#e2e8f0" strokeWidth="10" />
            <circle cx="60" cy="60" r="52" fill="none" stroke={ringColor} strokeWidth="10" strokeLinecap="round" strokeDasharray={`${dash} ${circumference}`} />
          </svg>
          <div className="absolute inset-0 flex flex-col items-center justify-center">
            <div className="text-3xl font-bold text-slate-800">{score}</div>
            <div className="text-[10px] text-slate-500 uppercase tracking-wide">out of 100</div>
          </div>
        </div>
        <div className="flex-1 min-w-0">
          <div className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold border ${gradeColor}`}>
            {score >= 75 ? <ShieldCheck className="w-3.5 h-3.5" /> : score >= 55 ? <ShieldAlert className="w-3.5 h-3.5" /> : <ShieldX className="w-3.5 h-3.5" />}
            {grade} deliverability
          </div>
          <p className="text-sm text-slate-700 mt-2">
            {score >= 90 && "Your email looks clean and is very likely to reach the inbox."}
            {score >= 75 && score < 90 && "Good. A few small tweaks could push you into the inbox more consistently."}
            {score >= 55 && score < 75 && "Several things may trip spam filters or hurt engagement. See recommendations below."}
            {score < 55 && "This email has serious issues likely to land it in spam folders. Fix the red items before sending."}
          </p>
          <div className="flex items-center gap-3 mt-2 text-xs text-slate-600">
            {issueCounts.bad > 0 && <span className="inline-flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-red-500" /> {issueCounts.bad} critical</span>}
            {issueCounts.warn > 0 && <span className="inline-flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-amber-500" /> {issueCounts.warn} warning{issueCounts.warn > 1 ? "s" : ""}</span>}
            <span className="inline-flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-emerald-500" /> {issueCounts.ok} passing</span>
          </div>
        </div>
      </div>

      <div className="space-y-2 max-h-[50vh] overflow-y-auto pr-1">
        {checks.map(c => (
          <div key={c.id} className={`flex items-start gap-3 p-3 rounded-lg border ${c.severity === "bad" ? "bg-red-50 border-red-200" : c.severity === "warn" ? "bg-amber-50 border-amber-200" : "bg-emerald-50 border-emerald-200"}`}>
            <div className="mt-0.5 flex-shrink-0">
              {c.severity === "bad" && <ShieldX className="w-4 h-4 text-red-600" />}
              {c.severity === "warn" && <ShieldAlert className="w-4 h-4 text-amber-600" />}
              {c.severity === "ok" && <CheckCircle2 className="w-4 h-4 text-emerald-600" />}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center justify-between gap-2">
                <div className={`text-sm font-semibold ${c.severity === "bad" ? "text-red-900" : c.severity === "warn" ? "text-amber-900" : "text-emerald-900"}`}>{c.title}</div>
                {c.weight > 0 && <span className="text-[10px] font-medium text-slate-500 whitespace-nowrap">−{c.weight} pts</span>}
              </div>
              <p className={`text-xs mt-0.5 ${c.severity === "bad" ? "text-red-800" : c.severity === "warn" ? "text-amber-800" : "text-emerald-800"}`}>{c.detail}</p>
            </div>
          </div>
        ))}
      </div>

      <p className="text-[11px] text-slate-500 leading-relaxed border-t border-slate-200 pt-3">
        This score uses common heuristics (subject quality, trigger words, image/text ratio, hidden content, link patterns) — the same factors used by Mail-Tester, GlockApps and SpamAssassin. For absolute inbox placement, also configure SPF, DKIM and DMARC for vertexclimate.co.uk.
      </p>
    </div>
  );
}

function BlockBtn({ icon: Icon, label, onClick }: { icon: typeof Heading1; label: string; onClick: () => void }) {
  return <button onClick={onClick} className="w-full flex items-center gap-2 px-3 py-2 text-sm rounded-lg border border-slate-200 hover:bg-slate-50 hover:border-primary text-left"><Icon className="w-4 h-4 text-slate-500" /> {label}</button>;
}

function ImageBlockEditor({ block, onChange }: { block: any; onChange: (p: any) => void }) {
  const [busy, setBusy] = useState(false);
  const onUpload = async () => {
    const f = await pickImageFile();
    if (!f) return;
    setBusy(true);
    try {
      const r = await uploadImageFile(f);
      onChange({ url: r.url });
    } catch (e: any) { alert("Upload failed: " + (e?.message || "unknown")); }
    finally { setBusy(false); }
  };
  return (
    <div className="space-y-2">
      <div className="flex gap-2">
        <input value={block.url || ""} onChange={e => onChange({ url: e.target.value })} placeholder="Paste image URL or click Upload" className="input flex-1" />
        <button type="button" onClick={onUpload} disabled={busy} className="px-3 py-1.5 text-sm rounded-lg bg-primary text-white disabled:opacity-50 flex items-center gap-1.5 whitespace-nowrap"><ImageIcon className="w-4 h-4" />{busy ? "Uploading…" : "Upload"}</button>
      </div>
      <input value={block.alt || ""} onChange={e => onChange({ alt: e.target.value })} placeholder="Alt text (for accessibility)" className="input" />
      {block.url && <img src={block.url} alt="" className="max-h-32 rounded border border-slate-200" />}
      <p className="text-xs text-slate-500">Uploaded images are auto-compressed and resized to ≤1400px wide. They render responsively in the email.</p>
    </div>
  );
}

function BlockEditor({ block, onChange, onMoveUp, onMoveDown, onRemove, canUp, canDown }: any) {
  return (
    <div className="border border-slate-200 rounded-lg p-3 group hover:border-primary/40">
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">{block.type}</span>
        <div className="flex gap-1 opacity-60 group-hover:opacity-100">
          <button disabled={!canUp} onClick={onMoveUp} className="p-1 rounded hover:bg-slate-100 disabled:opacity-30">↑</button>
          <button disabled={!canDown} onClick={onMoveDown} className="p-1 rounded hover:bg-slate-100 disabled:opacity-30">↓</button>
          <button onClick={onRemove} className="p-1 rounded hover:bg-rose-50 text-rose-500"><Trash2 className="w-3.5 h-3.5" /></button>
        </div>
      </div>
      {block.type === "heading" && (
        <div className="space-y-2">
          <input value={block.text} onChange={e => onChange({ text: e.target.value })} className="input" />
          <div className="grid grid-cols-2 gap-2">
            <select value={block.level} onChange={e => onChange({ level: Number(e.target.value) })} className="input"><option value={1}>H1 (large)</option><option value={2}>H2 (medium)</option><option value={3}>H3 (small)</option></select>
            <select value={block.align} onChange={e => onChange({ align: e.target.value })} className="input"><option value="left">Left</option><option value="center">Center</option><option value="right">Right</option></select>
            <select value={block.fontFamily || ""} onChange={e => onChange({ fontFamily: e.target.value || undefined })} className="input">{FONT_OPTIONS.map(f => <option key={f.label} value={f.value}>{f.label}</option>)}</select>
            <select value={block.fontSize || ""} onChange={e => onChange({ fontSize: e.target.value ? Number(e.target.value) : undefined })} className="input"><option value="">Default size</option>{HEADING_SIZES.map(s => <option key={s} value={s}>{s}px</option>)}</select>
          </div>
        </div>
      )}
      {block.type === "paragraph" && (
        <div className="space-y-2">
          <textarea value={block.text} onChange={e => onChange({ text: e.target.value })} rows={4} className="input" />
          <div className="grid grid-cols-3 gap-2">
            <select value={block.align} onChange={e => onChange({ align: e.target.value })} className="input"><option value="left">Left</option><option value="center">Center</option><option value="right">Right</option></select>
            <select value={block.fontFamily || ""} onChange={e => onChange({ fontFamily: e.target.value || undefined })} className="input">{FONT_OPTIONS.map(f => <option key={f.label} value={f.value}>{f.label}</option>)}</select>
            <select value={block.fontSize || ""} onChange={e => onChange({ fontSize: e.target.value ? Number(e.target.value) : undefined })} className="input"><option value="">Default size</option>{PARAGRAPH_SIZES.map(s => <option key={s} value={s}>{s}px</option>)}</select>
          </div>
        </div>
      )}
      {block.type === "image" && (
        <ImageBlockEditor block={block} onChange={onChange} />
      )}
      {block.type === "button" && (
        <div className="space-y-2">
          <input value={block.label} onChange={e => onChange({ label: e.target.value })} placeholder="Button label" className="input" />
          <input value={block.href} onChange={e => onChange({ href: e.target.value })} placeholder="https://..." className="input" />
          <select value={block.align} onChange={e => onChange({ align: e.target.value })} className="input"><option value="left">Left</option><option value="center">Center</option><option value="right">Right</option></select>
        </div>
      )}
      {block.type === "divider" && <div className="border-t border-slate-200 my-2" />}
      {block.type === "spacer" && (
        <div className="flex items-center gap-2 text-sm">
          <span className="text-slate-500">Height:</span>
          <input type="number" value={block.height || 24} onChange={e => onChange({ height: Number(e.target.value) })} className="input w-24" /> px
        </div>
      )}
    </div>
  );
}

// ---------------- Campaigns ----------------
function CampaignsView({ notify }: { notify: (m: string, k?: "ok" | "err") => void }) {
  const [rows, setRows] = useState<Campaign[]>([]);
  const [templates, setTemplates] = useState<Template[]>([]);
  const [groups, setGroups] = useState<Group[]>([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const load = async () => { setLoading(true); try { setRows(await api("/admin/newsletter/campaigns")); setTemplates(await api("/admin/newsletter/templates")); setGroups(await api("/admin/newsletter/groups")); } catch (e: any) { notify(e.message, "err"); } finally { setLoading(false); } };
  useEffect(() => { load(); const t = setInterval(load, 8000); return () => clearInterval(t); }, []);

  return (
    <div className="space-y-3">
      <div className="flex justify-between items-center bg-white p-3 rounded-xl border border-slate-200 shadow-sm">
        <h2 className="font-semibold text-slate-800">Campaigns</h2>
        <button onClick={() => setCreateOpen(true)} disabled={templates.length === 0} title={templates.length === 0 ? "Create a template first" : ""} className="px-3 py-2 text-sm rounded-lg bg-primary text-white flex items-center gap-1.5 disabled:opacity-50"><Plus className="w-4 h-4" /> New Campaign</button>
      </div>
      {loading ? <div className="p-12 text-center"><Loader2 className="w-6 h-6 animate-spin inline text-slate-500" /></div> : rows.length === 0 ? (
        <div className="p-12 text-center text-slate-500 bg-white rounded-xl border border-slate-200">No campaigns yet.</div>
      ) : (
        <div className="space-y-2">
          {rows.map(c => <CampaignRow key={c.id} c={c} templates={templates} groups={groups} onChanged={load} notify={notify} />)}
        </div>
      )}
      {createOpen && <NewCampaignModal templates={templates} groups={groups} onClose={() => setCreateOpen(false)} onDone={() => { setCreateOpen(false); load(); }} notify={notify} />}
    </div>
  );
}

function CampaignRow({ c, templates, groups, onChanged, notify }: { c: Campaign; templates: Template[]; groups: Group[]; onChanged: () => void; notify: (m: string, k?: "ok" | "err") => void }) {
  const pct = c.totalRecipients > 0 ? Math.round(((c.sentCount + c.failedCount) / c.totalRecipients) * 100) : 0;
  const tpl = templates.find(t => t.id === c.templateId);
  const send = async () => { if (!confirm(`Send to ${c.groupFilter && c.groupFilter.length ? "selected groups" : "all active subscribers"}?`)) return; try { await api(`/admin/newsletter/campaigns/${c.id}/send`, { method: "POST" }); notify("Campaign started"); onChanged(); } catch (e: any) { notify(e.message, "err"); } };
  const cancel = async () => { if (!confirm("Cancel sending?")) return; try { await api(`/admin/newsletter/campaigns/${c.id}/cancel`, { method: "POST" }); onChanged(); } catch (e: any) { notify(e.message, "err"); } };
  const dup = async () => { try { await api(`/admin/newsletter/campaigns/${c.id}/duplicate`, { method: "POST" }); notify("Duplicated"); onChanged(); } catch (e: any) { notify(e.message, "err"); } };
  const del = async () => { if (!confirm("Delete campaign?")) return; try { await api(`/admin/newsletter/campaigns/${c.id}`, { method: "DELETE" }); onChanged(); } catch (e: any) { notify(e.message, "err"); } };
  return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-4">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0">
          <div className="flex items-center gap-2"><h3 className="font-semibold text-slate-800">{c.name}</h3><Badge tone={c.status}>{c.status}</Badge></div>
          <p className="text-xs text-slate-500 mt-0.5">Template: {tpl?.name || "—"} · {c.totalRecipients} recipients{c.groupFilter && c.groupFilter.length > 0 ? ` · Groups: ${c.groupFilter.join(", ")}` : ""}</p>
        </div>
        <div className="flex gap-1.5">
          {c.status === "draft" && <button onClick={send} className="px-3 py-1.5 text-xs rounded-lg bg-emerald-600 text-white flex items-center gap-1"><Play className="w-3.5 h-3.5" /> Send</button>}
          {c.status === "sending" && <button onClick={cancel} className="px-3 py-1.5 text-xs rounded-lg bg-amber-600 text-white flex items-center gap-1"><Pause className="w-3.5 h-3.5" /> Cancel</button>}
          <button onClick={dup} className="p-1.5 rounded hover:bg-slate-100 text-slate-500" title="Duplicate"><Copy className="w-4 h-4" /></button>
          {c.status !== "sending" && <button onClick={del} className="p-1.5 rounded hover:bg-rose-50 text-rose-500"><Trash2 className="w-4 h-4" /></button>}
        </div>
      </div>
      {(c.status === "sending" || c.status === "completed") && c.totalRecipients > 0 && (
        <div className="mt-3">
          <div className="flex justify-between text-xs text-slate-500 mb-1"><span>{c.sentCount + c.failedCount} / {c.totalRecipients}</span><span>{pct}%</span></div>
          <div className="h-2 bg-slate-100 rounded-full overflow-hidden"><div className="h-full bg-primary rounded-full transition-all" style={{ width: `${pct}%` }} /></div>
        </div>
      )}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mt-3 pt-3 border-t border-slate-100 text-xs">
        <Stat label="Delivered" value={c.sentCount} />
        <Stat label="Failed" value={c.failedCount} />
        <Stat label="Opens" value={c.opensCount} />
        <Stat label="Clicks" value={c.clicksCount} />
      </div>
    </div>
  );
}

function Stat({ label, value }: { label: string; value: number }) {
  return <div><div className="text-slate-500">{label}</div><div className="font-semibold text-slate-800">{value}</div></div>;
}

function NewCampaignModal({ templates, groups, onClose, onDone, notify }: { templates: Template[]; groups: Group[]; onClose: () => void; onDone: () => void; notify: (m: string, k?: "ok" | "err") => void }) {
  const [name, setName] = useState("");
  const [templateId, setTemplateId] = useState<number | "">(templates[0]?.id || "");
  const [grp, setGrp] = useState<string[]>([]);
  const submit = async () => {
    try { await api("/admin/newsletter/campaigns", { method: "POST", body: JSON.stringify({ name: name || "Untitled campaign", templateId: templateId || null, groupFilter: grp }) }); onDone(); }
    catch (e: any) { notify(e.message, "err"); }
  };
  return (
    <Modal open onClose={onClose} title="New Campaign">
      <div className="space-y-3">
        <Field label="Name"><input value={name} onChange={e => setName(e.target.value)} className="input" /></Field>
        <Field label="Template"><select value={templateId} onChange={e => setTemplateId(Number(e.target.value))} className="input">{templates.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}</select></Field>
        {groups.length > 0 && (
          <Field label="Send to groups (leave empty = all active)">
            <div className="flex flex-wrap gap-2">
              {groups.map(g => <button key={g.id} type="button" onClick={() => setGrp(s => s.includes(g.name) ? s.filter(x => x !== g.name) : [...s, g.name])}
                className={`px-3 py-1 rounded-full text-xs font-medium border ${grp.includes(g.name) ? "bg-primary text-white border-primary" : "bg-white border-slate-200 text-slate-600"}`}>{g.name}</button>)}
            </div>
          </Field>
        )}
        <div className="flex justify-end gap-2"><button onClick={onClose} className="px-4 py-2 rounded-lg border border-slate-200 text-sm">Cancel</button><button onClick={submit} className="px-4 py-2 rounded-lg bg-primary text-white text-sm">Create</button></div>
      </div>
    </Modal>
  );
}

// ---------------- SMTP ----------------
function SmtpView({ notify }: { notify: (m: string, k?: "ok" | "err") => void }) {
  const [cfg, setCfg] = useState<any>(null);
  const [testTo, setTestTo] = useState("");
  const [verifying, setVerifying] = useState(false);
  const [sending, setSending] = useState(false);
  useEffect(() => { (async () => { try { setCfg(await api("/admin/newsletter/smtp")); } catch (e: any) { notify(e.message, "err"); } })(); }, []);
  const verify = async () => { setVerifying(true); try { await api("/admin/newsletter/smtp/verify", { method: "POST" }); notify("SMTP connection OK"); } catch (e: any) { notify(e.message, "err"); } finally { setVerifying(false); } };
  const test = async () => { setSending(true); try { await api("/admin/newsletter/smtp/test", { method: "POST", body: JSON.stringify({ to: testTo }) }); notify("Test email sent"); } catch (e: any) { notify(e.message, "err"); } finally { setSending(false); } };
  if (!cfg) return <div className="p-12 text-center"><Loader2 className="w-6 h-6 animate-spin inline text-slate-500" /></div>;
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
      <div className="bg-white p-5 rounded-xl border border-slate-200 shadow-sm">
        <h3 className="font-semibold text-slate-800 mb-3 flex items-center gap-2"><MailCheck className="w-5 h-5 text-primary" /> SMTP Configuration</h3>
        <p className="text-xs text-slate-500 mb-4">Configured from environment variables. Update secrets to change values.</p>
        <div className="space-y-1.5 text-sm">
          <InfoRow label="Host" value={cfg.host} />
          <InfoRow label="Port" value={cfg.port} />
          <InfoRow label="Secure (TLS)" value={cfg.secure ? "Yes" : "No"} />
          <InfoRow label="User" value={cfg.user} />
          <InfoRow label="From Name" value={cfg.fromName} />
          <InfoRow label="From Email" value={cfg.fromEmail} />
          <InfoRow label="Reply-To" value={cfg.replyTo} />
          <InfoRow label="App URL" value={cfg.appUrl} />
          <InfoRow label="Credentials" value={cfg.configured ? <span className="text-emerald-600 font-medium">Configured</span> : <span className="text-rose-600 font-medium">Missing SMTP_PASS</span>} />
        </div>
        <button onClick={verify} disabled={verifying} className="mt-4 w-full px-4 py-2 rounded-lg bg-slate-800 text-white text-sm disabled:opacity-50 flex items-center justify-center gap-2">{verifying ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />} Verify Connection</button>
      </div>
      <div className="bg-white p-5 rounded-xl border border-slate-200 shadow-sm">
        <h3 className="font-semibold text-slate-800 mb-3 flex items-center gap-2"><Send className="w-5 h-5 text-primary" /> Send Test Email</h3>
        <p className="text-xs text-slate-500 mb-4">Verify SMTP works by sending a test email to any address.</p>
        <Field label="Recipient email"><input value={testTo} onChange={e => setTestTo(e.target.value)} className="input" placeholder="you@example.com" /></Field>
        <button onClick={test} disabled={sending || !testTo} className="mt-3 w-full px-4 py-2 rounded-lg bg-primary text-white text-sm disabled:opacity-50">{sending ? "Sending…" : "Send Test"}</button>
      </div>
    </div>
  );
}

function InfoRow({ label, value }: { label: string; value: React.ReactNode }) {
  return <div className="flex justify-between py-1.5 border-b border-slate-100 last:border-0"><span className="text-slate-500">{label}</span><span className="font-medium text-slate-800 text-right break-all">{value || "—"}</span></div>;
}

// ---------------- Reports ----------------
function ReportsView({ notify }: { notify: (m: string, k?: "ok" | "err") => void }) {
  const [data, setData] = useState<any>(null);
  useEffect(() => { (async () => { try { setData(await api("/admin/newsletter/reports")); } catch (e: any) { notify(e.message, "err"); } })(); }, []);
  if (!data) return <div className="p-12 text-center"><Loader2 className="w-6 h-6 animate-spin inline text-slate-500" /></div>;
  const t = data.totals;
  const orate = t.total_sent > 0 ? Math.round((t.opens / t.total_sent) * 100) : 0;
  const crate = t.total_sent > 0 ? Math.round((t.clicks / t.total_sent) * 100) : 0;
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <StatTile label="Total Sent" value={t.total_sent} icon={Send} tone="blue" />
        <StatTile label="Failed" value={t.failed} icon={AlertCircle} tone="rose" />
        <StatTile label="Opens" value={`${t.opens} (${orate}%)`} icon={Eye} tone="emerald" />
        <StatTile label="Clicks" value={`${t.clicks} (${crate}%)`} icon={MousePointerClick} tone="amber" />
        <StatTile label="Unsubscribes" value={t.unsubs} icon={X} tone="rose" />
      </div>
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
        <div className="px-4 py-3 border-b border-slate-100"><h3 className="font-semibold text-slate-800">Campaign Performance</h3></div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-600">
              <tr><th className="p-3 text-left">Campaign</th><th className="p-3 text-left">Status</th><th className="p-3 text-right">Recipients</th><th className="p-3 text-right">Sent</th><th className="p-3 text-right">Opens</th><th className="p-3 text-right">Clicks</th><th className="p-3 text-right">Open %</th><th className="p-3 text-right">Click %</th></tr>
            </thead>
            <tbody>
              {data.campaigns.map((c: Campaign) => {
                const or = c.sentCount > 0 ? Math.round((c.opensCount / c.sentCount) * 100) : 0;
                const cr = c.sentCount > 0 ? Math.round((c.clicksCount / c.sentCount) * 100) : 0;
                return (
                  <tr key={c.id} className="border-t border-slate-100 hover:bg-slate-50">
                    <td className="p-3 font-medium text-slate-800">{c.name}</td>
                    <td className="p-3"><Badge tone={c.status}>{c.status}</Badge></td>
                    <td className="p-3 text-right">{c.totalRecipients}</td>
                    <td className="p-3 text-right">{c.sentCount}</td>
                    <td className="p-3 text-right">{c.opensCount}</td>
                    <td className="p-3 text-right">{c.clicksCount}</td>
                    <td className="p-3 text-right">{or}%</td>
                    <td className="p-3 text-right">{cr}%</td>
                  </tr>
                );
              })}
              {data.campaigns.length === 0 && <tr><td colSpan={8} className="p-8 text-center text-slate-500">No campaigns yet</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

// ---------------- Settings ----------------
function SettingsView({ notify }: { notify: (m: string, k?: "ok" | "err") => void }) {
  const [s, setS] = useState<SettingsRow | null>(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => { (async () => { try { setS(await api("/admin/newsletter/settings")); } catch (e: any) { notify(e.message, "err"); } })(); }, []);
  const update = (patch: Partial<SettingsRow>) => setS((p: SettingsRow | null) => p ? { ...p, ...patch } : p);
  const save = async () => { if (!s) return; setSaving(true); try { const r = await api("/admin/newsletter/settings", { method: "PUT", body: JSON.stringify(s) }); setS(r); notify("Settings saved"); } catch (e: any) { notify(e.message, "err"); } finally { setSaving(false); } };
  if (!s) return <div className="p-12 text-center"><Loader2 className="w-6 h-6 animate-spin inline text-slate-500" /></div>;
  const socials = s.socialLinks || [];
  return (
    <div className="space-y-3 max-w-3xl">
      <Section title="Email Header">
        <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={s.showHeader} onChange={e => update({ showHeader: e.target.checked })} /> Show header banner</label>
        <Field label="Logo URL"><input value={s.logoUrl || ""} onChange={e => update({ logoUrl: e.target.value })} className="input" placeholder="https://..." /></Field>
        <div className="grid grid-cols-3 gap-3">
          <Field label="Logo width (px)"><input type="number" value={s.logoWidth} onChange={e => update({ logoWidth: Number(e.target.value) })} className="input" /></Field>
          <Field label="Header background"><input type="color" value={s.headerBackground} onChange={e => update({ headerBackground: e.target.value })} className="input h-10" /></Field>
          <Field label="Accent color"><input type="color" value={s.accentColor} onChange={e => update({ accentColor: e.target.value })} className="input h-10" /></Field>
        </div>
        <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={s.showAccentBar} onChange={e => update({ showAccentBar: e.target.checked })} /> Show accent bar under header</label>
      </Section>

      <Section title="Social Links (footer)">
        <div className="space-y-2">
          {socials.map((sl, idx) => (
            <div key={idx} className="flex gap-2">
              <input value={sl.network} onChange={e => update({ socialLinks: socials.map((x, i) => i === idx ? { ...x, network: e.target.value } : x) })} placeholder="Facebook" className="input w-40" />
              <input value={sl.url} onChange={e => update({ socialLinks: socials.map((x, i) => i === idx ? { ...x, url: e.target.value } : x) })} placeholder="https://..." className="input flex-1" />
              <button onClick={() => update({ socialLinks: socials.filter((_, i) => i !== idx) })} className="p-2 rounded hover:bg-rose-50 text-rose-500"><Trash2 className="w-4 h-4" /></button>
            </div>
          ))}
          <button onClick={() => update({ socialLinks: [...socials, { network: "", url: "" }] })} className="px-3 py-1.5 text-xs rounded-lg border border-slate-200 hover:bg-slate-50 flex items-center gap-1.5"><Plus className="w-4 h-4" /> Add social link</button>
        </div>
      </Section>

      <Section title="Business Identity">
        <Field label="Business name"><input value={s.businessName || ""} onChange={e => update({ businessName: e.target.value })} className="input" /></Field>
        <Field label="Business address"><textarea value={s.businessAddress || ""} onChange={e => update({ businessAddress: e.target.value })} rows={2} className="input" /></Field>
      </Section>

      <Section title="Sender Defaults">
        <Field label="Default From Name"><input value={s.defaultFromName || ""} onChange={e => update({ defaultFromName: e.target.value })} className="input" placeholder="VertexAir Climate Systems" /></Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Default From Email"><input value={s.defaultFromEmail || ""} onChange={e => update({ defaultFromEmail: e.target.value })} className="input" /></Field>
          <Field label="Reply-To Email"><input value={s.replyToEmail || ""} onChange={e => update({ replyToEmail: e.target.value })} className="input" /></Field>
        </div>
      </Section>

      <Section title="Sending & Tracking">
        <Field label="Rate limit (emails per minute)"><input type="number" min={1} max={300} value={s.emailsPerMinute} onChange={e => update({ emailsPerMinute: Number(e.target.value) })} className="input w-32" /></Field>
        <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={s.trackOpens} onChange={e => update({ trackOpens: e.target.checked })} /> Track opens (pixel)</label>
        <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={s.trackClicks} onChange={e => update({ trackClicks: e.target.checked })} /> Track clicks (link rewriting)</label>
      </Section>

      <div className="flex justify-end"><button onClick={save} disabled={saving} className="px-5 py-2.5 rounded-lg bg-primary text-white text-sm font-medium disabled:opacity-50">{saving ? "Saving…" : "Save Settings"}</button></div>
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm space-y-3"><h3 className="font-semibold text-slate-800">{title}</h3>{children}</div>;
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return <label className="block"><span className="text-xs font-medium text-slate-600 mb-1 block">{label}</span>{children}</label>;
}

const GOOGLE_FONTS = [
  "Inter", "Roboto", "Open Sans", "Lato", "Montserrat", "Poppins",
  "Playfair Display", "Merriweather", "Source Sans 3", "Nunito",
  "Oswald", "Raleway", "Work Sans", "PT Sans", "Noto Sans",
];
const SYSTEM_FONTS = ["Arial", "Helvetica", "Georgia", "Times New Roman", "Courier New", "Verdana", "Tahoma", "Trebuchet MS"];
const FONT_SIZES = [
  { label: "Small", value: "12px" },
  { label: "Normal", value: "15px" },
  { label: "Medium", value: "18px" },
  { label: "Large", value: "22px" },
  { label: "Huge", value: "28px" },
];
const COLOR_SWATCHES = ["#000000", "#374151", "#6b7280", "#ef4444", "#f59e0b", "#10b981", "#06b6d4", "#1d4ed8", "#7c3aed", "#db2777", "#ffffff"];

function RichTextEditor({ value, onChange, minRows = 14 }: { value: string; onChange: (v: string) => void; minRows?: number }) {
  const ref = useRef<HTMLDivElement>(null);
  const [showFont, setShowFont] = useState(false);
  const [showSize, setShowSize] = useState(false);
  const [showColor, setShowColor] = useState(false);
  const [showBg, setShowBg] = useState(false);
  const savedRange = useRef<Range | null>(null);
  const lastEmittedRef = useRef<string>(value || "");
  const didMountRef = useRef(false);

  useEffect(() => {
    if (!ref.current) return;
    const incoming = value || "";
    if (!didMountRef.current) {
      ref.current.innerHTML = incoming;
      lastEmittedRef.current = ref.current.innerHTML;
      didMountRef.current = true;
      return;
    }
    if (incoming === lastEmittedRef.current) return;
    if (ref.current.innerHTML === incoming) { lastEmittedRef.current = incoming; return; }
    if (document.activeElement === ref.current) return;
    ref.current.innerHTML = incoming;
    lastEmittedRef.current = ref.current.innerHTML;
  }, [value]);

  function saveSelection() {
    const sel = window.getSelection();
    if (sel && sel.rangeCount > 0 && ref.current && ref.current.contains(sel.anchorNode)) {
      savedRange.current = sel.getRangeAt(0).cloneRange();
    }
  }
  function restoreSelection() {
    const sel = window.getSelection();
    if (sel && savedRange.current) {
      sel.removeAllRanges();
      sel.addRange(savedRange.current);
    }
  }
  function emit() {
    if (!ref.current) return;
    const html = ref.current.innerHTML;
    lastEmittedRef.current = html;
    onChange(html);
  }
  function exec(cmd: string, val?: string) {
    ref.current?.focus();
    restoreSelection();
    document.execCommand(cmd, false, val);
    emit();
  }
  function setFont(name: string) { exec("fontName", name); setShowFont(false); }
  function setSize(px: string) {
    ref.current?.focus(); restoreSelection();
    const sel = window.getSelection();
    if (!sel || sel.rangeCount === 0) return;
    const range = sel.getRangeAt(0);
    if (range.collapsed) { setShowSize(false); return; }
    const span = document.createElement("span");
    span.style.fontSize = px;
    try { range.surroundContents(span); } catch {
      const frag = range.extractContents(); span.appendChild(frag); range.insertNode(span);
    }
    sel.removeAllRanges();
    const r = document.createRange(); r.selectNodeContents(span); sel.addRange(r);
    emit(); setShowSize(false);
  }
  function setColor(c: string) { exec("foreColor", c); setShowColor(false); }
  function setBg(c: string) { exec("hiliteColor", c); setShowBg(false); }
  function insertLink() {
    saveSelection();
    const sel = window.getSelection();
    const selectedText = sel ? sel.toString() : "";
    const url = window.prompt("Link URL", "https://");
    if (!url) return;
    restoreSelection();
    if (selectedText) {
      exec("createLink", url);
    } else {
      const html = `<a href="${url.replace(/"/g, "&quot;")}" target="_blank" rel="noopener noreferrer">${url.replace(/[&<>]/g, c => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;" }[c]!))}</a>`;
      exec("insertHTML", html);
    }
  }
  async function insertImage() {
    saveSelection();
    const choice = window.confirm("OK to upload from your computer (auto-compressed & responsive)\nCancel to paste an image URL instead");
    let url = "";
    if (choice) {
      const f = await pickImageFile();
      if (!f) return;
      try {
        const r = await uploadImageFile(f);
        url = r.url;
      } catch (e: any) { alert("Upload failed: " + (e?.message || "unknown")); return; }
    } else {
      const u = window.prompt("Image URL (https://...)", "https://");
      if (!u || !/^https?:\/\//i.test(u)) return;
      url = u;
    }
    restoreSelection();
    const html = `<img src="${url.replace(/"/g, "&quot;")}" alt="" style="max-width:100%;height:auto;display:block;border-radius:6px;" />`;
    exec("insertHTML", html);
  }
  function clearFormat() { exec("removeFormat"); exec("unlink"); }

  const Btn = ({ children, title, onClick, active }: { children: React.ReactNode; title: string; onClick: () => void; active?: boolean }) => (
    <button type="button" title={title} onMouseDown={e => { e.preventDefault(); saveSelection(); }} onClick={onClick} className={`h-8 min-w-[32px] px-1.5 inline-flex items-center justify-center text-xs rounded hover:bg-slate-200 ${active ? "bg-slate-200" : ""}`}>{children}</button>
  );
  const Sep = () => <span className="w-px h-5 bg-slate-300 mx-1 self-center" />;

  return (
    <div className="border border-slate-200 rounded-lg overflow-visible">
      <div className="flex flex-wrap items-center gap-0.5 px-2 py-1.5 bg-slate-50 border-b border-slate-200 sticky top-0 z-10">
        <Btn title="Undo" onClick={() => exec("undo")}>↶</Btn>
        <Btn title="Redo" onClick={() => exec("redo")}>↷</Btn>
        <Sep />
        <div className="relative">
          <button type="button" onMouseDown={e => { e.preventDefault(); saveSelection(); }} onClick={() => setShowFont(v => !v)} className="h-8 px-2 inline-flex items-center gap-1 text-xs rounded hover:bg-slate-200 min-w-[110px] justify-between">Font<ChevronDown className="w-3 h-3" /></button>
          {showFont && (
            <div className="absolute top-full left-0 mt-1 bg-white border border-slate-200 rounded-lg shadow-lg z-20 max-h-72 overflow-y-auto min-w-[180px]">
              {SYSTEM_FONTS.map(f => <button key={f} onClick={() => setFont(f)} className="block w-full text-left px-3 py-1.5 text-sm hover:bg-slate-100" style={{ fontFamily: f }}>{f}</button>)}
              <div className="border-t border-slate-200 my-1" />
              <div className="px-3 py-1 text-[10px] uppercase font-semibold text-slate-400">Google Fonts</div>
              {GOOGLE_FONTS.map(f => <button key={f} onClick={() => setFont(f)} className="block w-full text-left px-3 py-1.5 text-sm hover:bg-slate-100" style={{ fontFamily: `'${f}', sans-serif` }}>{f}</button>)}
            </div>
          )}
        </div>
        <div className="relative">
          <button type="button" onMouseDown={e => { e.preventDefault(); saveSelection(); }} onClick={() => setShowSize(v => !v)} className="h-8 px-2 inline-flex items-center gap-1 text-xs rounded hover:bg-slate-200" title="Font size"><Type className="w-3.5 h-3.5" /><ChevronDown className="w-3 h-3" /></button>
          {showSize && (
            <div className="absolute top-full left-0 mt-1 bg-white border border-slate-200 rounded-lg shadow-lg z-20 min-w-[120px]">
              {FONT_SIZES.map(s => <button key={s.value} onClick={() => setSize(s.value)} className="block w-full text-left px-3 py-1.5 text-sm hover:bg-slate-100" style={{ fontSize: s.value }}>{s.label}</button>)}
            </div>
          )}
        </div>
        <Sep />
        <Btn title="Bold" onClick={() => exec("bold")}><b>B</b></Btn>
        <Btn title="Italic" onClick={() => exec("italic")}><i>I</i></Btn>
        <Btn title="Underline" onClick={() => exec("underline")}><u>U</u></Btn>
        <Btn title="Strikethrough" onClick={() => exec("strikeThrough")}><s>S</s></Btn>
        <div className="relative">
          <button type="button" onMouseDown={e => { e.preventDefault(); saveSelection(); }} onClick={() => setShowColor(v => !v)} className="h-8 px-1.5 inline-flex items-center gap-1 text-xs rounded hover:bg-slate-200" title="Text color"><span className="font-bold">A</span><span className="w-3 h-1 bg-red-500 rounded-sm" /></button>
          {showColor && <ColorGrid onPick={setColor} />}
        </div>
        <div className="relative">
          <button type="button" onMouseDown={e => { e.preventDefault(); saveSelection(); }} onClick={() => setShowBg(v => !v)} className="h-8 px-1.5 inline-flex items-center gap-1 text-xs rounded hover:bg-slate-200" title="Highlight color">🖌</button>
          {showBg && <ColorGrid onPick={setBg} />}
        </div>
        <Sep />
        <Btn title="Align left" onClick={() => exec("justifyLeft")}>⇤</Btn>
        <Btn title="Align center" onClick={() => exec("justifyCenter")}>≡</Btn>
        <Btn title="Align right" onClick={() => exec("justifyRight")}>⇥</Btn>
        <Sep />
        <Btn title="Bullet list" onClick={() => exec("insertUnorderedList")}>•</Btn>
        <Btn title="Numbered list" onClick={() => exec("insertOrderedList")}>1.</Btn>
        <Btn title="Decrease indent" onClick={() => exec("outdent")}>⇤|</Btn>
        <Btn title="Increase indent" onClick={() => exec("indent")}>|⇥</Btn>
        <Btn title="Quote" onClick={() => exec("formatBlock", "blockquote")}>“”</Btn>
        <Sep />
        <Btn title="Insert link" onClick={insertLink}><Link2 className="w-3.5 h-3.5" /></Btn>
        <Btn title="Insert image" onClick={insertImage}><ImageIcon className="w-3.5 h-3.5" /></Btn>
        <Btn title="Remove formatting" onClick={clearFormat}>✕<sub>fmt</sub></Btn>
      </div>
      <div
        ref={ref}
        contentEditable
        suppressContentEditableWarning
        onInput={emit}
        onBlur={emit}
        className="p-4 text-sm outline-none prose prose-sm max-w-none [&_blockquote]:border-l-4 [&_blockquote]:border-slate-300 [&_blockquote]:pl-3 [&_blockquote]:text-slate-600 [&_ul]:list-disc [&_ul]:pl-6 [&_ol]:list-decimal [&_ol]:pl-6 [&_h1]:text-2xl [&_h1]:font-bold [&_h2]:text-xl [&_h2]:font-bold [&_h3]:text-lg [&_h3]:font-semibold [&_a]:text-blue-600 [&_a]:underline [&_img]:max-w-full [&_img]:h-auto"
        style={{ fontFamily: "Arial, sans-serif", minHeight: `${minRows * 22}px` }}
      />
    </div>
  );
}

function ColorGrid({ onPick }: { onPick: (c: string) => void }) {
  return (
    <div className="absolute top-full left-0 mt-1 bg-white border border-slate-200 rounded-lg shadow-lg z-20 p-2 grid grid-cols-6 gap-1">
      {COLOR_SWATCHES.map(c => (
        <button key={c} onClick={() => onPick(c)} className="w-6 h-6 rounded border border-slate-200 hover:scale-110 transition-transform" style={{ background: c }} title={c} />
      ))}
    </div>
  );
}
